<template>
  <section class="admin-content" id="population">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3"><i class="fe fe-users" style="font-size: 22px;"></i></div>
            <div>
              <h4 class="mb-0 font-weight-bold">Population &amp; coverage</h4>
              <small style="opacity: 0.8;">How much of the population is covered by health insurance, towards universal health coverage</small>
            </div>
          </div>
        </div>
      </div>
      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="error" class="card shadow-sm table-card mb-4"><div class="card-body empty-state"><i class="fe fe-alert-circle"></i>{{ error }}</div></div>

          <template v-else>
            <div class="row mb-2" v-if="summary">
              <div class="col-6 col-md-3 mb-3" v-for="c in cards" :key="c.label">
                <div class="card shadow-sm h-100"><div class="card-body py-3">
                  <span class="stat-label">{{ c.label }}</span>
                  <div class="stat-value" :class="c.cls">{{ c.value }}</div>
                  <small class="text-muted">{{ c.note }}</small>
                </div></div>
              </div>
            </div>

            <EnrolleeMap title="Health insurance coverage" subtitle="enrollees as a share of the population" initial-metric="coverage" :key="mapKey" />

            <!-- LGAs -->
            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header flex-wrap">
                <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
                <div class="flex-grow-1">
                  <h6 class="mb-0 font-weight-bold text-white">Coverage by LGA</h6>
                  <small style="color: rgba(255,255,255,.75);">An LGA's population is the sum of its wards; the state's is the sum of its LGAs. Lowest coverage first.</small>
                </div>
                <button v-if="canEdit" class="btn btn-sm btn-light" @click="downloadTemplate"><i class="fe fe-download mr-1"></i> CSV template</button>
                <label v-if="canEdit" class="btn btn-sm btn-light mb-0"><i class="fe fe-upload mr-1"></i> Import CSV
                  <input type="file" accept=".csv,text/csv" class="d-none" @change="importCsv" /></label>
              </div>
              <div class="card-body">
                <div v-if="importResult" class="alert py-2 small" :class="importResult.unmatched.length ? 'alert-warning' : 'alert-success'">
                  {{ importResult.message }}
                  <ul v-if="importResult.unmatched.length" class="mb-0 mt-1">
                    <li v-for="u in importResult.unmatched.slice(0, 15)" :key="u.line">Line {{ u.line }}: {{ [u.lga, u.ward].filter(Boolean).join(" / ") }} — {{ u.reason }}</li>
                  </ul>
                </div>
                <p v-if="canEdit" class="small text-muted">
                  Download the template, fill the <b>population</b> column (e.g. from census projections or GRID3 ward estimates) and import it, or open an LGA below and type the figures.
                  Rows are matched by ward_id, or by LGA and ward name.
                </p>
                <div v-if="loading" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
                <div v-else class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="table-head"><tr>
                      <th class="table-th">LGA</th><th class="table-th text-right">Population</th><th class="table-th text-right">Enrollees</th>
                      <th class="table-th" style="min-width: 180px;">Coverage</th><th class="table-th text-right">Not covered</th><th class="table-th text-right">Wards with figures</th><th class="table-th"></th>
                    </tr></thead>
                    <tbody>
                      <tr class="table-row" v-for="r in lgaRows" :key="r.id">
                        <td>{{ r.name }}</td>
                        <td class="text-right">{{ fmt(r.population) }}</td>
                        <td class="text-right">{{ fmt(r.enrollees) }}</td>
                        <td>
                          <div v-if="r.coverage_pct !== null" class="d-flex align-items-center" style="gap: 8px;">
                            <div class="progress flex-grow-1" style="height: 8px;"><div class="progress-bar" :class="barClass(r.coverage_pct)" :style="{ width: Math.min(100, r.coverage_pct) + '%' }"></div></div>
                            <small class="font-weight-bold" style="width: 52px; text-align: right;">{{ r.coverage_pct }}%</small>
                          </div>
                          <small v-else class="text-muted">No population</small>
                        </td>
                        <td class="text-right">{{ fmt(r.uncovered) }}</td>
                        <td class="text-right"><span :class="r.lgaInfo && r.lgaInfo.complete ? 'text-success' : 'text-warning'">{{ r.lgaInfo ? `${r.lgaInfo.wards_with_population} / ${r.lgaInfo.wards}` : "—" }}</span></td>
                        <td class="text-right"><button class="btn btn-sm btn-outline-primary" @click="openLga(r)">{{ canEdit ? "Wards" : "View wards" }}</button></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Wards of one LGA -->
            <div v-if="lga" class="card shadow-sm table-card mb-4" ref="wards">
              <div class="form-card-header flex-wrap">
                <div class="section-number"><i class="fe fe-edit-2" style="font-size: 12px;"></i></div>
                <div class="flex-grow-1">
                  <h6 class="mb-0 font-weight-bold text-white">Wards of {{ lga.name }}</h6>
                  <small style="color: rgba(255,255,255,.75);">Population {{ fmt(wardTotal) }} (sum of the wards){{ canEdit ? " · leave a ward empty if its figure is unknown" : "" }}</small>
                </div>
                <button class="btn btn-sm btn-light" @click="lga = null"><i class="fe fe-x"></i></button>
              </div>
              <div class="card-body">
                <div v-if="canEdit" class="form-row">
                  <div class="form-group col-md-3"><label class="small font-weight-bold text-secondary">Year of the figures</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="year" min="1950" max="2100" /></div>
                  <div class="form-group col-md-5"><label class="small font-weight-bold text-secondary">Source</label>
                    <input class="form-control form-control-sm" v-model="source" maxlength="150" placeholder="e.g. NPC 2006 projected, GRID3 2024" /></div>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="table-head"><tr><th class="table-th">Ward</th><th class="table-th text-right" style="width: 200px;">Population</th><th class="table-th text-right">Enrollees</th><th class="table-th text-right">Coverage</th><th class="table-th">Figures</th></tr></thead>
                    <tbody>
                      <tr class="table-row" v-for="w in wards" :key="w.id">
                        <td>{{ w.name }}</td>
                        <td class="text-right">
                          <input v-if="canEdit" type="number" min="0" class="form-control form-control-sm text-right" v-model="w.edit" />
                          <span v-else>{{ fmt(w.population) }}</span>
                        </td>
                        <td class="text-right">{{ fmt(w.enrollees) }}</td>
                        <td class="text-right">{{ w.population ? ((100 * (w.enrollees || 0)) / w.population).toFixed(2) + "%" : "—" }}</td>
                        <td><small class="text-muted">{{ [w.year, w.source].filter(Boolean).join(" · ") || "—" }}</small></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="canEdit" class="d-flex mt-3" style="gap: 8px;">
                  <button class="btn btn-sm btn-submit" :disabled="saving" @click="saveWards">{{ saving ? "Saving…" : "Save populations" }}</button>
                  <small class="text-muted align-self-center">{{ changed }} changed</small>
                </div>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import EnrolleeMap from "./components/EnrolleeMap.vue";

/** Ward populations and coverage (population/coverage, population/wards, population/import). */
export default {
  components: { Navbar, EnrolleeMap },
  data() {
    return {
      loading: false, error: "", summary: null, rows: [], lgas: [], canEdit: false,
      lga: null, wards: [], year: new Date().getFullYear(), source: "", saving: false, importResult: null, mapKey: 0,
    };
  },
  computed: {
    lgaRows() {
      const info = Object.fromEntries(this.lgas.map((l) => [l.id, l]));
      return this.rows.map((r) => ({ ...r, lgaInfo: info[r.id] }));
    },
    cards() {
      const s = this.summary;
      return [
        { label: `Population${s.year ? " (" + s.year + ")" : ""}`, value: this.fmt(s.population), note: s.complete ? "All wards have figures" : `${s.wards_with_population} of ${s.wards} wards have figures` },
        { label: "Enrollees", value: this.fmt(s.enrollees), note: "principals and dependants" },
        { label: "Coverage", value: s.coverage_pct === null ? "—" : s.coverage_pct + "%", cls: s.coverage_pct === null ? "" : s.coverage_pct >= 50 ? "text-success" : s.coverage_pct >= 20 ? "text-warning" : "text-danger", note: "of the population" },
        { label: "Not yet covered", value: this.fmt(s.uncovered), note: "the gap to universal coverage" },
      ];
    },
    wardTotal() {
      return this.wards.reduce((n, w) => n + (w.edit === "" || w.edit === null ? 0 : Number(w.edit)), 0);
    },
    changed() {
      return this.wards.filter((w) => String(w.edit ?? "") !== String(w.population ?? "")).length;
    },
  },
  mounted() { this.load(); },
  methods: {
    fmt(v) { return v === null || v === undefined ? "—" : Number(v).toLocaleString(); },
    barClass(p) { return p >= 50 ? "bg-success" : p >= 20 ? "bg-warning" : "bg-danger"; },
    msg(e, fallback) { return (e.response && e.response.data && (e.response.data.message || "")) || fallback; },
    async load() {
      this.loading = true;
      try {
        const [cov, w] = await Promise.all([this.axios.get("/api/v1/auth/population/coverage"), this.axios.get("/api/v1/auth/population/wards").catch(() => null)]);
        this.summary = cov.data.data.summary;
        this.rows = cov.data.data.rows;
        this.canEdit = cov.data.can_edit;
        if (w) this.lgas = w.data.data.lgas;
      } catch (e) {
        this.error = this.msg(e, "Could not load the population figures.");
      } finally {
        this.loading = false;
      }
    },
    async openLga(r) {
      this.lga = r;
      const [w, cov] = await Promise.all([
        this.axios.get("/api/v1/auth/population/wards", { params: { lga_id: r.id } }),
        this.axios.get("/api/v1/auth/population/coverage", { params: { level: "ward", lga_id: r.id } }),
      ]);
      const enrolled = Object.fromEntries(cov.data.data.rows.map((x) => [x.id, x.enrollees]));
      this.lgas = w.data.data.lgas;
      this.wards = w.data.data.wards.map((x) => ({ ...x, enrollees: enrolled[x.id] || 0, edit: x.population === null ? "" : x.population }));
      const withYear = this.wards.find((x) => x.year);
      if (withYear) { this.year = withYear.year; this.source = withYear.source || ""; }
      this.$nextTick(() => this.$refs.wards && this.$refs.wards.scrollIntoView({ behavior: "smooth" }));
    },
    async saveWards() {
      this.saving = true;
      try {
        const wards = this.wards.map((w) => ({ id: w.id, population: w.edit === "" || w.edit === null ? null : Number(w.edit) }));
        const res = await this.axios.put("/api/v1/auth/population/wards", { year: this.year || null, source: this.source || null, wards });
        this.$toasted.success(res.data.message, { position: "top-center", duration: 3000 });
        await this.load();
        await this.openLga(this.rows.find((r) => r.id === this.lga.id) || this.lga);
        this.mapKey++;
      } catch (e) {
        this.$toasted.error(this.msg(e, "Could not save."), { position: "top-center", duration: 4000 });
      } finally {
        this.saving = false;
      }
    },
    async downloadTemplate() {
      const res = await this.axios.get("/api/v1/auth/population/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "ward_population.csv";
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    },
    async importCsv(ev) {
      const file = ev.target.files[0];
      ev.target.value = "";
      if (!file) return;
      const year = window.prompt("Year of these population figures?", String(this.year));
      if (year === null) return;
      const source = window.prompt("Source of the figures (optional)?", this.source) || "";
      const form = new FormData();
      form.append("file", file);
      if (year) form.append("year", year);
      if (source) form.append("source", source);
      try {
        const res = await this.axios.post("/api/v1/auth/population/import", form);
        this.importResult = res.data;
        await this.load();
        this.mapKey++;
      } catch (e) {
        this.$toasted.error(this.msg(e, "Could not import the file."), { position: "top-center", duration: 5000 });
      }
    },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
</style>
